import {Button, withWidth} from '@material-ui/core';
import {WithWidth} from '@material-ui/core/withWidth';
import AddAlertButton from 'Core/components/Alerts/AddAlertButton';
import CoreRouteConstants from 'Core/CoreRouteConstants';
import {AlertFilters, AlertPayload, changeAlertsFilter, deleteAlert, EmptyAlertFilter, getAlerts} from 'Core/ducks/alerts';
import {addFilterParameters} from 'Shared/utilities/addFilterParameters';
import {ALERT_ROOT_URI} from 'Core/api/alertApi';
import {AsyncPage} from 'Core/models/Async';
import {Page} from 'Core/models/Page';
import React, {useEffect} from 'react';
import {connect, useDispatch, useSelector} from 'react-redux';
import {RouteComponentProps, withRouter} from 'react-router';
import {ReducerState} from 'rootReducer';
import Column from 'Shared/components/PagedView/Column';
import {
  withDateFilterProperties,
  withSelectFilterProperties,
  withStringFilterProperties,
} from 'Shared/components/PagedView/Filter';
import PagedView from 'Shared/components/PagedView/PagedView';
import {toFormattedDate} from 'Shared/utilities/DateFormatter';
import {withValueChangedHook} from 'Shared/utilities/withValueChanged';
import {ValidAgentNumber} from 'Users/models/AgentNumber';
import {selectCurrentAgentNumbers} from 'Users/selectors/userProfileSelectors';
import './AlertsAdministration.sass';
import {LinesOfBusiness} from 'Users/models/LineOfBusiness';

type AlertsProps = {
  agentNumbers: ValidAgentNumber[];
  getAlerts: typeof getAlerts;
  alerts: AsyncPage<Page<AlertPayload>>;
  alertFilters: AlertFilters;
  changeAlertsFilter: typeof changeAlertsFilter;
  deleteAlert: typeof deleteAlert;
} & RouteComponentProps &
  WithWidth;

export const Alerts = (props: AlertsProps) => {
  const dispatch = useDispatch();
  const {alertFilters} = useSelector((state: ReducerState) => state.alerts);

  const onAddAlert = () => props.history.push(CoreRouteConstants.ADD_ALERT);
  const onEditAlert = (alert: AlertPayload) => props.history.push(CoreRouteConstants.EDIT_ALERT(alert.id!));
  const onDeleteAlert = (alert: AlertPayload) => props.deleteAlert(alert.id!);
  const changeFilter = withValueChangedHook({...alertFilters, ['pageNumber']: '1'}, changeAlertsFilter, dispatch);
  const filterProperties = {
    changeFilter,
    filterData: alertFilters,
  };

  useEffect(() => {
    props.getAlerts(undefined, props.alertFilters);
  }, [props.alertFilters]);

  useEffect(() => {
      dispatch(changeAlertsFilter({...EmptyAlertFilter, pageNumber: '1'}));
  }, [changeAlertsFilter]);

  return (
    <>
      <PagedView
        pagedViewKey="alertsAdministration"
        loadMore={page => {
          const filters = props.alertFilters;
          const nextPage: number = parseInt(filters.pageNumber) + 1;
          if (Math.ceil(page.totalCount / parseInt(filters.pageSize)) > parseInt(filters.pageNumber)) {
            filters.pageNumber = nextPage.toString();

            const link = addFilterParameters(filters);

            page.links = [
              {
                href: ALERT_ROOT_URI + link,
                method: 'GET',
                rel: 'nextpage',
              },
            ];
          }
          props.getAlerts(page, filters);
        }}
        data={props.alerts}
        actions={<AddAlertButton {...props} onAddAlert={onAddAlert} />}
      >
        {columnProps => (
          <>
            <Column
              {...columnProps}
              title="Line of Business"
              filterProperties={withSelectFilterProperties(filterProperties, 'applicationId', LinesOfBusiness)}
              default
            >
              {alert => alert.applications?.map(application => application.name!)?.join(', ')}
            </Column>
            <Column
              {...columnProps}
              title="Title"
              filterProperties={withStringFilterProperties(filterProperties, 'title')}
              default
            >
              {alert => alert.title}
            </Column>
            <Column
              {...columnProps}
              title="Details"
              filterProperties={withStringFilterProperties(filterProperties, 'description')}
              default
            >
              {alert => alert.description}
            </Column>
            <Column
              {...columnProps}
              title="Start Date"
              filterProperties={withDateFilterProperties(filterProperties, 'startDate')}
              default
            >
              {alert => toFormattedDate(alert.startDate)}
            </Column>
            <Column
              {...columnProps}
              title="End Date"
              filterProperties={withDateFilterProperties(filterProperties, 'endDate')}
              default
            >
              {alert => toFormattedDate(alert.endDate)}
            </Column>
            <Column {...columnProps} isActions title="Actions" default>
              {alert => (
                <>
                  <Button color="primary" onClick={() => onEditAlert(alert)}>
                    Edit
                  </Button>
                  <Button color="primary" onClick={() => onDeleteAlert(alert)}>
                    Delete
                  </Button>
                </>
              )}
            </Column>
          </>
        )}
      </PagedView>
    </>
  );
};

const mapStateToProps = (state: ReducerState) => ({
  agentNumbers: selectCurrentAgentNumbers(state),
  alertFilters: state.alerts.alertFilters,
  alerts: state.alerts.alerts,
});

export default withRouter(connect(mapStateToProps, {getAlerts, deleteAlert, changeAlertsFilter})(withWidth()(Alerts)));
